import { useState, useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";

import type { IButton } from "libs/design-system/src/types/Button/button";

import {
  SHARED_PAYMENT_LINKS_EVENTS,
  SHARED_PAYMENT_LINKS_EVENTS_PARAMS,
  SHARED_PAYMENT_LINKS_EVENTS_VALUES
} from "@app/dashboard/constants/tracking_events/payment_links/shared";

import { COUNTRIES_INFO } from "@app/dashboard/constants/countries";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";

import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";

import { getMerchantIntegrationsCurrencies } from "@app/dashboard/services/inner_layout/bulk_payment_links/get-integrations-currencies";
import { getMerchantPaymentMethods } from "@app/dashboard/services/inner_layout/bulk_payment_links/get-payment-methods";
import { getMerchantPreSelectedMethods } from "@app/dashboard/services/inner_layout/bulk_payment_links/get-selected-methods";

import type { IFullPageStatus } from "@app/dashboard/types/innerLayout/payment_links/shared";
import type {
  TSelectedMethod
} from "@app/dashboard/types/innerLayout/payment_links/bulk/sections/payment-section";

import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { CARD_PAYMENT, DEFAULT_CURRENCY, reorderPaymentMethods } from "../utils";
import { getLocalizedCurrency } from "@app/dashboard/utils/convertAmounts";
import { FormatCurrencyForDisplay } from "@unified-checkout/design-system";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";


export const usePaymentSectionLogic = ({
  setFullPageStatus
}: {
  setFullPageStatus?: Dispatch<SetStateAction<IFullPageStatus>>
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLive } = useTestLiveModeStore();

  const country = getCountryFromDomain();
  const countryCurrency = COUNTRIES_INFO[country]?.currency || DEFAULT_CURRENCY;

  const canViewPaymentIntegrations = isAccessible(PERMISSIONS.CAN_VIEW_PAYMENT_INTEGRATIONS);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCurrency, setCurrentCurrency] = useState<string>();
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [tempSelectedMethods, setTempSelectedMethods] = useState<TSelectedMethod>();
  const [savedMethods, setSavedMethods] = useState<TSelectedMethod>();
  const [isPmsInitialized, setIsPmsInitialized] = useState(false);

  const {
    isLoading: isIntegrationsCurrenciesLoading,
    isError: isIntegrationsCurrenciesError,
    data: integrationsCurrencies
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MERCHANT_INTEGRATIONS_CURRENCIES, isLive],
    queryFn: () => getMerchantIntegrationsCurrencies({ isLive }).then(res => res.data)
  });

  const {
    isLoading: isPaymentMethodsLoading,
    isRefetching: isPaymentMethodsRefetching,
    isError: isPaymentMethodsError,
    data: paymentMethods
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MERCHANT_PAYMENT_METHODS, isLive, currentCurrency],
    queryFn: () => getMerchantPaymentMethods({ currency: currentCurrency as string, isLive }).then(res => res.data),
    enabled: !!currentCurrency && canViewPaymentIntegrations
  });

  const {
    isLoading: isPreSelectedMethodsLoading,
    isError: isPreSelectedMethodsError,
    data: preSelectedMethods
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MERCHANT_PRESELECTED_METHODS, isLive],
    queryFn: () => getMerchantPreSelectedMethods({ isLive }).then(res => res.data)
  });


  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_MERCHANT_INTEGRATIONS_CURRENCIES, isLive] });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_MERCHANT_PAYMENT_METHODS, isLive, currentCurrency] });
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_MERCHANT_PRESELECTED_METHODS, isLive] });
    };
  }, []);

  useEffect(() => {
    integrationsCurrencies && setAvailableCurrencies(integrationsCurrencies);
    integrationsCurrencies?.length === 1 && setCurrentCurrency(integrationsCurrencies[0]);
  }, [integrationsCurrencies]);

  useEffect(() => {
    if (paymentMethods || !canViewPaymentIntegrations) {
      setIsPmsInitialized(true);
    }
  }, [paymentMethods, canViewPaymentIntegrations]);

  useEffect(() => {
    setFullPageStatus?.(prev => ({
      ...prev,
      error: isPaymentMethodsError || isPreSelectedMethodsError,
      loading: !isPmsInitialized
    }));
  }, [
    isPmsInitialized,
    isPaymentMethodsError,
    isPreSelectedMethodsError,
  ]);

  useEffect(() => {
    if (isPreSelectedMethodsLoading) return;

    if (!Object.keys(preSelectedMethods || {}).length) {
      // return prev first to avoid overriding the currency selected by the user with the default one
      setCurrentCurrency((prev) => prev || countryCurrency);
    }

    const selected = Object.entries(preSelectedMethods || {})?.reduce((acc: TSelectedMethod, gateway: any) => {
      const [gatewayName, method] = gateway;
      setCurrentCurrency(prev => method[0].currency || prev || countryCurrency);

      acc[gatewayName] = {
        gatewayName,
        id: method[0].id,
        integrationName: method[0].integration_name
      };
      return acc;
    }, {});

    setTempSelectedMethods(selected);
    setSavedMethods(selected);

  }, [preSelectedMethods, isPreSelectedMethodsLoading]);

  const modalActionBtns: IButton[] = [
    {
      text: t('innerLayout.paymentLinksCreationPages.actions.saveMethods'),
      onClickHandler: () => {
        const methodsWithIntegrationsSelectedOnly = Object.fromEntries(
          Object.entries(tempSelectedMethods || {}).filter(([_, method]) => method.id)
        );

        setSavedMethods(methodsWithIntegrationsSelectedOnly);
        setTempSelectedMethods(methodsWithIntegrationsSelectedOnly);
        setIsModalOpen(false);

        const selectedMethodsCount = Object.keys(methodsWithIntegrationsSelectedOnly).length;
        const selectedMethods = Object.values(methodsWithIntegrationsSelectedOnly).map(method => method.gatewayName);
        const cardIntegrationId = Object.values(methodsWithIntegrationsSelectedOnly)
          .find(method =>
            method.gatewayName === CARD_PAYMENT ||
            method.integrationName === SHARED_PAYMENT_LINKS_EVENTS_VALUES.CARD
          )?.id;

        amplitude.track(SHARED_PAYMENT_LINKS_EVENTS.PAYMENT_METHODS_SAVED, {
          [SHARED_PAYMENT_LINKS_EVENTS_PARAMS.SELECTED_METHODS_COUNT]: selectedMethodsCount,
          [SHARED_PAYMENT_LINKS_EVENTS_PARAMS.SELECTED_METHODS]: selectedMethods,
          [SHARED_PAYMENT_LINKS_EVENTS_PARAMS.CARD_INTEGRATION_ID]: cardIntegrationId,
          [SHARED_PAYMENT_LINKS_EVENTS_PARAMS.HAS_CARD_METHODS]: !!cardIntegrationId
        });
      },
      disabled:
        !(Object.keys(tempSelectedMethods || {}).length) ||
        !Object.values(tempSelectedMethods || {}).some(method => method.id) ||
        JSON.stringify(tempSelectedMethods) === JSON.stringify(savedMethods),
    },
    {
      color: "secondary",
      variant: "outlined",
      text: t('form.cancel'),
      onClickHandler: () => {
        setIsModalOpen(false);
        setTempSelectedMethods(savedMethods);
      }
    }
  ];

  const isMethodsLoading = isPaymentMethodsLoading || isPreSelectedMethodsLoading;

  const canChangeCurrency =
    !isPaymentMethodsLoading &&
    !isPaymentMethodsRefetching &&
    !isPaymentMethodsError;

  const orderedPaymentMethods = reorderPaymentMethods(paymentMethods, preSelectedMethods);

  const getFormattedCurrency = (currency: string) => {
    return getLocalizedCurrency(t, (FormatCurrencyForDisplay(currency) as any));
  };


  return {
    isIntegrationsCurrenciesLoading,
    isIntegrationsCurrenciesError,
    integrationsCurrencies,

    isPaymentMethodsLoading,
    isPaymentMethodsRefetching,
    isPaymentMethodsError,
    paymentMethods,

    isPreSelectedMethodsLoading,
    isPreSelectedMethodsError,
    preSelectedMethods,

    currentCurrency,
    setCurrentCurrency,

    availableCurrencies,
    setAvailableCurrencies,

    tempSelectedMethods,
    setTempSelectedMethods,

    savedMethods,
    setSavedMethods,

    isModalOpen,
    setIsModalOpen,

    modalActionBtns,
    isMethodsLoading,
    canChangeCurrency,
    orderedPaymentMethods,
    getFormattedCurrency,
    canViewPaymentIntegrations
  };
};